import { Asset, AssetTypeOption, AssetStatusOption } from '../types';

const API_BASE = '/api/v1';

export interface CreateAssetRequest {
  inventoryNumber: string;
  name: string;
  assetType: string;
  status?: string;
  siteId?: string;
  roomId?: string;
  purchaseDate?: string;
  notes?: string;
  linkedToId?: string;
}

export interface UpdateAssetRequest {
  inventoryNumber?: string;
  name?: string;
  assetType?: string;
  status?: string;
  siteId?: string | null;
  roomId?: string | null;
  purchaseDate?: string | null;
  notes?: string | null;
  linkedToId?: string | null;
}

export interface AssetsListResponse {
  assets: Asset[];
  total: number;
}

export interface AssetTypesResponse {
  types: AssetTypeOption[];
}

export interface AssetStatusesResponse {
  statuses: AssetStatusOption[];
}

export interface AssetSummaryResponse {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
}

export interface AssetFilters {
  status?: string;
  assetType?: string;
  siteId?: string;
  roomId?: string;
  search?: string;
}

export async function fetchAssets(filters?: AssetFilters): Promise<AssetsListResponse> {
  const params = new URLSearchParams();

  if (filters?.status) params.append('status', filters.status);
  if (filters?.assetType) params.append('assetType', filters.assetType);
  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.roomId) params.append('roomId', filters.roomId);
  if (filters?.search) params.append('search', filters.search);

  const url = params.toString()
    ? `${API_BASE}/assets?${params.toString()}`
    : `${API_BASE}/assets`;

  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch assets: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchAsset(id: string): Promise<{ asset: Asset }> {
  const response = await fetch(`${API_BASE}/assets/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch asset: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchAssetTypes(): Promise<AssetTypesResponse> {
  const response = await fetch(`${API_BASE}/assets/types`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch asset types: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchAssetStatuses(): Promise<AssetStatusesResponse> {
  const response = await fetch(`${API_BASE}/assets/statuses`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch asset statuses: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchAssetSummary(): Promise<AssetSummaryResponse> {
  const response = await fetch(`${API_BASE}/assets/summary`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch asset summary: ${response.statusText}`);
  }

  return response.json();
}

export async function createAsset(data: CreateAssetRequest): Promise<{ success: boolean; asset: Asset }> {
  const response = await fetch(`${API_BASE}/assets`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to create asset');
  }

  return response.json();
}

export async function updateAsset(id: string, data: UpdateAssetRequest): Promise<{ success: boolean; asset: Asset }> {
  const response = await fetch(`${API_BASE}/assets/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to update asset');
  }

  return response.json();
}

export async function deleteAsset(id: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/assets/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to delete asset');
  }

  return response.json();
}

export async function exportAssets(filters?: AssetFilters): Promise<Blob> {
  const params = new URLSearchParams();

  if (filters?.status) params.append('status', filters.status);
  if (filters?.assetType) params.append('assetType', filters.assetType);
  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.roomId) params.append('roomId', filters.roomId);
  if (filters?.search) params.append('search', filters.search);

  const url = params.toString()
    ? `${API_BASE}/assets/export?${params.toString()}`
    : `${API_BASE}/assets/export`;

  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to export assets: ${response.statusText}`);
  }

  return response.blob();
}

export async function syncDevicesFromOcs(): Promise<{ success: boolean; created: number; skipped: number; message: string }> {
  const response = await fetch(`${API_BASE}/assets/sync-devices`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to sync devices');
  }

  return response.json();
}

export interface AvailableDevice {
  id: string;
  hostname: string;
  deviceType: string;
  site: string | null;
  room: string | null;
}

export async function fetchAvailableDevices(): Promise<{ devices: AvailableDevice[]; total: number }> {
  const response = await fetch(`${API_BASE}/assets/available-devices`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch available devices: ${response.statusText}`);
  }

  return response.json();
}
